import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TabDef } from '../../_models/components/tab-def';
import { RolePermissionsService } from '../../_services/role-permissions.service';
import { UserService } from '../../_services/user.service';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';

@Component({
  selector: 'share-module-tabs',
  templateUrl: './share-module-tabs.component.html',
  styleUrls: ['./share-module-tabs.component.scss']
})
export class ShareModuleTabsComponent implements OnInit, OnChanges, OnDestroy {
  /** All tabs for this module — component filters them by role. */
  @Input() tabs: TabDef[] = [];
  /** Module name passed to RolePermissionsService.filterTabs(). Omit to skip permission filtering. */
  @Input() module = '';
  /** The currently active tab key — keeps the strip in sync with parent route state. */
  @Input() activeKey = '';

  /** Emits the key of the tab the user clicked. Parent is responsible for route navigation. */
  @Output() tabChange = new EventEmitter<string>();

  filteredTabs: TabDef[] = [];
  activeIndex = 0;

  private role = '';
  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private rolePermissionsService: RolePermissionsService,
    private authz: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.userService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      const newRole = user?.role?.name ?? '';
      if (newRole !== this.role) {
        this.role = newRole;
        this.applyFilter();
      }
    });

    // Permission-tagged tabs (tab.permission) re-filter whenever the loaded
    // permission set changes, independent of the role-based path above.
    combineLatest([this.authz.permissions$, this.authz.status$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, status]) => {
        if (status === 'loaded' || status === 'error') this.applyFilter();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs']) {
      this.applyFilter();
    } else if (changes['activeKey']) {
      this.syncIndex();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(event: { index: number }): void {
    const tab = this.filteredTabs[event.index];
    if (tab) this.tabChange.emit(tab.key);
  }

  private applyFilter(): void {
    // Legacy per-module/tab-key role lookup, applied first when a `module`
    // is bound (unchanged behavior for tabs that don't opt into the newer
    // per-tab `permission`/`fallbackRoles` fields on TabDef).
    const roleFiltered =
      this.module && this.role
        ? this.rolePermissionsService.filterTabs(this.module, this.tabs, this.role)
        : [...this.tabs];

    // Tabs carrying `permission` are then re-checked against the loaded
    // permission set (with `fallbackRoles` as a migration safety net) -
    // this can only narrow, never widen, what the role-based pass allowed.
    this.filteredTabs = roleFiltered.filter((tab) => {
      if (!tab.permission) return true;
      const granted = Array.isArray(tab.permission)
        ? this.authz.hasAnyPermission(tab.permission)
        : this.authz.hasPermission(tab.permission);
      if (granted) return true;
      return !!tab.fallbackRoles && this.authz.hasAnyCurrentRole(tab.fallbackRoles);
    });
    this.syncIndex();
  }

  private syncIndex(): void {
    const idx = this.filteredTabs.findIndex((t) => t.key === this.activeKey);
    this.activeIndex = idx >= 0 ? idx : 0;
  }
}
